import axios, { type AxiosRequestConfig } from 'axios'

export const http = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/service/v2',
  timeout: 20000,
  withCredentials: true,
  headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }
})

function clearSessionAndRedirect() {
  localStorage.removeItem('v2_access_token')
  localStorage.removeItem('v2_user_info')
  localStorage.removeItem('ucode')
  localStorage.removeItem('user_info')
  if (!location.pathname.startsWith('/login')) {
    const redirect = `${location.pathname}${location.search}${location.hash}`
    location.replace(`/login?redirect=${encodeURIComponent(redirect)}`)
  }
}

http.interceptors.request.use((config) => {
  if (config.url?.startsWith('/v2/')) config.url = config.url.slice(3)
  const token = localStorage.getItem('v2_access_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

http.interceptors.response.use(
  (response) => {
    const refreshedToken = response.headers['x-access-token']
    if (refreshedToken) localStorage.setItem('v2_access_token', refreshedToken)
    return response
  },
  (error) => {
    const url = String(error.config?.url || '')
    if (error.response?.status === 401 && !url.includes('/auth/site') && !url.includes('/auth/captcha') && !url.includes('/auth/login')) {
      clearSessionAndRedirect()
    }
    return Promise.reject(new Error(error.response?.data?.msg || error.response?.data?.message || error.message))
  }
)

export interface PageResult<T = Record<string, unknown>> {
  list: T[]
  total: number
  raw: unknown
}

export function unwrap<T = unknown>(payload: any): T {
  if (payload == null) return payload
  if ((payload.status !== undefined || payload.code !== undefined) && payload.data !== undefined) return payload.data as T
  if (payload.data?.data !== undefined) return payload.data.data as T
  if (payload.data !== undefined) return payload.data as T
  return payload as T
}

export function unwrapPage<T = Record<string, unknown>>(payload: any): PageResult<T> {
  const root = payload?.data ?? payload ?? {}
  const page = root?.data ?? root
  const list = Array.isArray(page) ? page : Array.isArray(page?.data) ? page.data : []
  return {
    list,
    // `/users` 等接口返回 Result<{ data: [], total: number }>；列表被展开后，
    // total 位于 root 层，不能退回为当前页 list.length。
    total: Number(page?.totalCount ?? page?.total ?? root?.totalCount ?? root?.total ?? list.length),
    raw: payload
  }
}

export async function request<T = unknown>(config: AxiosRequestConfig): Promise<T> {
  const response = await http.request(config)
  const payload = response.data
  const code = payload?.code
  if (code != null && Number(code) !== 200) {
    throw new Error(payload?.message || payload?.msg || payload?.statusMsg || '请求失败')
  }
  return payload as T
}

export async function fetchPage<T = Record<string, unknown>>(
  url: string,
  params: Record<string, unknown>,
  method: 'GET' | 'POST' = 'POST'
) {
  const config: AxiosRequestConfig = { url, method }
  if (method === 'GET') config.params = params
  else {
    const { paged = 1, pageSize = 20, ...filters } = params
    config.params = { paged, pageSize }
    config.data = filters
  }
  return unwrapPage<T>(await request(config))
}
